import React, { useState, useEffect, useMemo } from 'react';
import { Table, notification, theme } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PlusOutlined } from '@ant-design/icons';
import { RootState } from 'store/configureStore';
import { evidencesOp } from 'store/ducks/Evidences';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { ISelectionOption, IUserInfo, YesNo } from 'store/ducks/globalTypes';
import Helpers from 'utils/Helpers';
import ComplianceEstLayout from 'components/Layouts/ComplianceEstLayout';
import CustomSelect from 'components/CustomSelect';
import CustomFileUpload from 'components/CustomFileUpload';
import CustomIconButton from 'components/CustomIconButton';
import TextAreaBoxEvidences from 'components/TextAreaBoxEvidences';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import ApiService from 'utils/ApiCalls';
import { RowEvidences } from 'store/ducks/Evidences/types';
import CustomDatePicker from 'components/CustomDatePicker';
import dayjs from 'dayjs';
import * as Styled from './EvidenceDefinition.styled';

const buildRows = (data: any[]): RowEvidences[] => {
  const rows: RowEvidences[] = [];

  (data || []).forEach((item) => {
    const solutions = Object.values(item.SOLUTIONS || {});

    if (!solutions.length) {
      rows.push({
        key: `bru-${item.id}`,
        interpretation: item.bru_desc,

        bruKey: item.bru_desc,
        solKey: `empty-${item.id}`,

        originalItem: item,
        solution: null,
        evidence: null
      });

      return;
    }

    solutions.forEach((sol: any) => {
      const evidences = Object.values(sol.EVIDENCES || {});

      if (!evidences.length) {
        rows.push({
          key: `empty-evd-${item.id}-${sol.sol_id}`,

          interpretation: item.bru_desc,

          sol_id: sol.sol_id,
          sol_desc: sol.sol_desc,
          sol_type: sol.sol_type,

          bruKey: item.bru_desc,
          solKey: `${item.id}-${sol.sol_id}`,

          originalItem: item,
          solution: sol,
          evidence: null,

          isEmptyEvidencePlaceholder: true
        });

        return;
      }

      evidences.forEach((evd: any) => {
        rows.push({
          key: `evd-${evd.evd_id}`,

          interpretation: item.bru_desc,

          sol_id: sol.sol_id,
          sol_desc: sol.sol_desc,
          sol_type: sol.sol_type,

          evd_id: evd.evd_id,
          evd_desc: evd.evd_desc,
          evd_file: evd.evd_file,
          evd_approver_ref: evd.evd_approver_ref,
          evd_approved: evd.evd_approved,
          evd_valid_from: evd.evd_valid_from,
          evd_valid_to: evd.evd_valid_to,

          bruKey: item.bru_desc,
          solKey: `${item.id}-${sol.sol_id}`,

          originalItem: item,
          solution: sol,
          evidence: evd
        });
      });
    });
  });

  return rows;
};

/**
 * ROW SPAN CALCULATION
 */
const addRowSpans = (rows: RowEvidences[]): RowEvidences[] => {
  const bruCount = new Map<string, number>();
  const solCount = new Map<string, number>();

  rows.forEach((r) => {
    bruCount.set(r.bruKey, (bruCount.get(r.bruKey) || 0) + 1);
    solCount.set(r.solKey, (solCount.get(r.solKey) || 0) + 1);
  });

  const seenBru = new Set<string>();
  const seenSol = new Set<string>();

  return rows.map((row, index) => {
    const firstBru = !seenBru.has(row.bruKey);
    const firstSol = !seenSol.has(row.solKey);

    seenBru.add(row.bruKey);
    seenSol.add(row.solKey);

    const isLastSolutionRow =
      index === rows.length - 1 || rows[index + 1].solKey !== row.solKey;

    return {
      ...row,
      bruRowSpan: firstBru ? bruCount.get(row.bruKey) : 0,
      solRowSpan: firstSol ? solCount.get(row.solKey) : 0,
      isLastSolutionRow
    };
  });
};

const { useToken } = theme;

const EvidenceDefinition: React.FC = () => {
  const dispatch = useDispatch();

  const { evidences, saveClicked, evidencesToSave, orgContext, lang } =
    useSelector((state: RootState) => ({
      evidences: state.evidences.evidences,
      saveClicked: state.complianceEst.saveClicked,
      evidencesToSave: state.evidences.evidencesToSave,
      orgContext: state.global.hierarchicalLevelsScope,
      lang: state.global.lang
    }));

  const { t } = useTranslation();
  const { token } = useToken();

  const [data, setData] = useState<RowEvidences[]>([]);
  const [page, setPage] = useState(1);
  const [approversList, setApproversList] = useState<ISelectionOption[]>([]);
  const [permitted, setPermitted] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const filterParams = useMemo(
    () => Helpers.toApiFilters(orgContext),
    [orgContext]
  );

  /**
   * LOAD DATA → FLATTEN → ADD ROW SPANS
   */
  useEffect(() => {
    if (!evidences) return;

    const raw = evidences?.flat?.() ?? evidences;

    const sorted = [...raw].sort((a, b) => {
      const aHasEvidence = Object.values(a.SOLUTIONS || {}).some(
        (sol: any) => Object.keys(sol.EVIDENCES || {}).length > 0
      );

      const bHasEvidence = Object.values(b.SOLUTIONS || {}).some(
        (sol: any) => Object.keys(sol.EVIDENCES || {}).length > 0
      );

      return Number(bHasEvidence) - Number(aHasEvidence);
    });

    const flat = buildRows(sorted);
    const withSpans = addRowSpans(flat);

    setData(withSpans);
  }, [evidences]);

  /**
   * APPROVERS
   */
  useEffect(() => {
    evidencesOp.fetchEvidenceDefApproversList().then((response) => {
      const options = Helpers.convertToSelectOptions(response?.approvers);
      setApproversList(options);
    });
  }, []);

  /**
   * PERMISSIONS
   */
  useEffect(() => {
    const userInfo: IUserInfo = Helpers.getUserInfo()?.user;

    if (userInfo?.entitlements?.includes('Second Approval')) {
      setPermitted(true);
    }
  }, []);

  /**
   * SAVE DISABLE FLAG
   */

  useEffect(() => {
    dispatch(complianceEstOp.setSaveDisabled(!isDirty));
  }, [isDirty]);

  /**
   * SAVE LOGIC
   */

  useEffect(() => {
    if (!saveClicked) return;

    const save = async () => {
      try {
        const newEvidencesToSave = data
          .filter((row) => row.solution && (row.isNew || row.evd_id))
          .map((row) => ({
            bru_id: row.originalItem?.id,
            due_date: null,
            evd_id: row.evd_id,
            evd_desc: row.evd_desc ?? '',
            evd_file: Array.isArray(row.evd_file) ? row.evd_file : [],
            evd_approver_ref:
              row.evd_approver_ref ||
              row.evidence?.evd_approver_ref ||
              'TBD@TBD.com',
            evd_valid_from:
              row.evd_valid_from ||
              row.evidence?.evd_valid_from ||
              new Date().toISOString().split('T')[0],
            evd_valid_to:
              row.evd_valid_to ||
              row.evidence?.evd_valid_to ||
              new Date().toISOString().split('T')[0],
            sol_id: row.sol_id
          }));
        await ApiService.saveEvidencesDef(newEvidencesToSave, lang);
        setIsDirty(false);
        dispatch(complianceEstOp.setSaveDisabled(true));
        dispatch(complianceEstOp.setSaveClicked(false));

        notification.success({
          message: 'Success',
          description: 'Evidence successfully saved'
        });
      } catch (error) {
        console.error(error);
        notification.error({
          message: 'Error',
          description: 'Failed to save evidence'
        });
      }
    };

    save();
  }, [saveClicked]);

  useEffect(() => {
    dispatch(complianceEstOp.setSaveDisabled(!isDirty));
  }, [isDirty, dispatch]);

  const handleDeleteEvidence = async (row: RowEvidences) => {
    if (!row.evd_id) {
      setData((prev) => addRowSpans(prev.filter((r) => r.key !== row.key)));
      return;
    }

    try {
      const updated = await evidencesOp.deleteEvidence(
        row.evd_id,
        lang,
        filterParams
      );

      if (updated) {
        dispatch(evidencesOp.setEvidences(updated.flat?.() ?? updated));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const updateRow = (key: string, field: keyof RowEvidences, value: any) => {
    setData((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              [field]: value
            }
          : row
      )
    );
    setIsDirty(true);
  };

  const addEvidenceRow = (row: RowEvidences) => {
    setData((prev) => {
      let updated = [...prev];

      const rowIndex = updated.findIndex((r) => r.key === row.key);

      const isEmptyPlaceholder = !row.evd_id && !row.evd_desc && !row.isNew;
      const newEvidenceId = generateUniqueId();

      const newRow: RowEvidences = {
        key: `new-${newEvidenceId}`,
        isNew: true,

        evd_id: newEvidenceId,

        interpretation: row.interpretation,

        sol_id: row.sol_id,
        sol_desc: row.sol_desc,
        sol_type: row.sol_type,

        evd_desc: '',
        evd_file: [],
        evd_approver_ref: 'TBD@TBD.com',
        evd_approved: 'N',

        evd_valid_from: '',
        evd_valid_to: '',

        bruKey: row.bruKey,
        solKey: row.solKey,

        originalItem: row.originalItem,
        solution: row.solution,

        evidence: {
          evd_id: newEvidenceId,
          evd_approver_ref: 'TBD@TBD.com',
          evd_valid_from: '',
          evd_valid_to: ''
        }
      };

      if (isEmptyPlaceholder) {
        updated.splice(rowIndex, 1, newRow);
      } else {
        const lastIndex = updated.findLastIndex((r) => r.solKey === row.solKey);

        updated.splice(lastIndex + 1, 0, newRow);
      }

      return addRowSpans(updated);
    });
  };

  const dataWithSpan = useMemo(() => {
    return addRowSpans(data);
  }, [data]);

  const paginated = useMemo(() => {
    const start = (page - 1) * 10;
    return data.slice(start, start + 10);
  }, [data, page]);

  const pageData = useMemo(() => {
    return addRowSpans(paginated);
  }, [paginated]);

  /**
   * STATUS CHANGE
   */
  const handleStatusChange = (value: YesNo, evId: string | number) => {
    switch (value) {
      case YesNo.YES:
        evidencesOp.approveEvidence(evId);
        break;
      case YesNo.NO:
        evidencesOp.rejectEvidence(evId);
        break;
    }
  };

  const columns = [
    /**
     * INTERPRETATION
     */
    {
      title: t('REGULATORY_REQUIREMENTS'),
      dataIndex: 'interpretation',
      key: 'interpretation',
      width: 300,
      render: (value: string, row: RowEvidences) => ({
        children: <Styled.MainTextBox>{value}</Styled.MainTextBox>,
        props: {
          rowSpan: row.bruRowSpan
        }
      })
    },

    /**
     * SOLUTION DESCRIPTION
     */
    {
      title: t('SOLUTION_DEFINITION'),
      dataIndex: 'sol_desc',
      key: 'sol_desc',
      width: 300,
      render: (value: string, row: RowEvidences) => {
        const hasSolution = Boolean(row.solution);

        return {
          children: hasSolution ? (
            <Styled.MainTextBox>{value}</Styled.MainTextBox>
          ) : null,
          props: {
            rowSpan: row.solRowSpan
          }
        };
      }
    },

    /**
     * SOLUTION TYPE
     */
    {
      title: t('SOLUTION_TYPE'),
      dataIndex: 'sol_type',
      key: 'sol_type',
      width: 150,
      render: (value: string, row: RowEvidences) => {
        const hasSolution = Boolean(row.solution);
        const isFirstSolRow = (row.solRowSpan ?? 0) > 0;

        if (!hasSolution) {
          return {
            children: null,
            props: {
              rowSpan: row.solRowSpan
            }
          };
        }

        return {
          children: isFirstSolRow ? (
            <CustomSelect width={100} defaultValue={value} disabled />
          ) : null,
          props: {
            rowSpan: row.solRowSpan
          }
        };
      }
    },

    /**
     * EVIDENCE DEFINITION
     */
    {
      title: t('EVIDENCE_DEFINITION'),
      dataIndex: 'evd_desc',
      key: 'evd_desc',
      width: 300,
      render: (value: string, row: RowEvidences, index: number) => {
        const isNew = row.key.startsWith('new-');
        const isLastRowOfSolution =
          index === data.length - 1 || data[index + 1]?.solKey !== row.solKey;

        return {
          children: (
            <div>
              {/* CONTENT */}
              {row.isEmptyEvidencePlaceholder ? null : (
                <TextAreaBoxEvidences
                  evidences={row as any}
                  evidenceId={row.evd_id}
                  value={value}
                  onChange={(newValue: string) => {
                    updateRow(row.key, 'evd_desc', newValue);
                  }}
                  onDelete={() => handleDeleteEvidence(row)}
                />
              )}

              {/* ACTIONS */}
              <Styled.ButtonContainer>
                {row.solution && isLastRowOfSolution && (
                  <CustomIconButton
                    type='primary'
                    shape='circle'
                    icon={<PlusOutlined />}
                    size='middle'
                    onClick={() => {
                      if (!row.solution) {
                        notification.warning({
                          message: 'Cannot add evidence',
                          description:
                            'You cannot add an evidence because it has no Solution related to it.'
                        });
                        return;
                      }

                      addEvidenceRow(row);
                    }}
                  />
                )}
              </Styled.ButtonContainer>
            </div>
          ),
          props: { rowSpan: 1 }
        };
      }
    },

    /**
     * EVIDENCE FILE
     */
    {
      title: t('EVIDENCE_FILE'),
      dataIndex: 'evd_file',
      key: 'evd_file',
      width: 150,
      render: (value: any, row: RowEvidences) => {
        const hasSolution = !!row.solution || row.isNew;

        if (!hasSolution) {
          return {
            children: null,
            props: { rowSpan: 1 }
          };
        }

        return {
          children: (
            <CustomFileUpload
              defaultFileList={Array.isArray(row.evd_file) ? row.evd_file : []}
              evidenceId={row.evd_id}
              evidenceData={row.evidence ?? row}
            />
          ),
          props: { rowSpan: 1 }
        };
      }
    },

    /**
     * APPROVER
     */
    {
      title: t('APPROVER'),
      dataIndex: 'evd_approver_ref',
      key: 'approver',
      width: 150,
      render: (value: string, row: RowEvidences) => {
        const hasSolution = !!row.solution || row.isNew;

        if (!hasSolution) {
          return {
            children: null,
            props: { rowSpan: 1 }
          };
        }

        return {
          children: (
            <CustomSelect
              size='lg'
              options={[
                { label: 'TBD@TBD.com', value: 'TBD@TBD.com' },
                ...approversList
              ]}
              value={row.evd_approver_ref || 'TBD@TBD.com'}
              onChange={(v: string) => {
                updateRow(row.key, 'evd_approver_ref', v);

                if (row.evd_id) {
                  updateRow(row.key, 'evd_approver_ref', v);
                }
              }}
            />
          ),
          props: { rowSpan: 1 }
        };
      }
    },

    /**
     * APPROVAL STATUS
     */

    {
      title: t('APPROVAL'),
      dataIndex: 'evd_approved',
      key: 'approval',
      width: 150,
      render: (value: string, row: RowEvidences) => {
        const hasSolution = !!row.solution || row.isNew;

        if (!hasSolution) {
          return {
            children: null,
            props: { rowSpan: 1 }
          };
        }

        const isApprover =
          row.evd_approver_ref === (Helpers.getUserInfo()?.user?.email || '');

        return {
          children: (
            <CustomSelect
              size='lg'
              options={[
                { label: 'Approved', value: YesNo.YES },
                { label: 'Not approved', value: YesNo.NO }
              ]}
              value={row.evd_approved === 'Y' ? YesNo.YES : YesNo.NO}
              disabled={row.isNew || !permitted || !isApprover}
              onChange={(v: YesNo) => {
                if (row.evd_id) {
                  handleStatusChange(v, row.evd_id);
                }
              }}
            />
          ),
          props: { rowSpan: 1 }
        };
      }
    },

    /**
     * VALID FROM
     */
    {
      title: t('info.evidence.validFrom'),
      dataIndex: 'evd_valid_from',
      key: 'evd_valid_from',
      width: 150,
      render: (value: string, row: RowEvidences) => {
        const hasSolution = !!row.solution || row.isNew;

        if (!hasSolution) {
          return {
            children: null,
            props: { rowSpan: 1 }
          };
        }

        return {
          children: (
            <CustomDatePicker
              value={row.evd_valid_from ? dayjs(row.evd_valid_from) : null}
              onChange={(date: any) =>
                updateRow(
                  row.key,
                  'evd_valid_from',
                  date ? date.format('YYYY-MM-DD') : null
                )
              }
            />
          ),
          props: { rowSpan: 1 }
        };
      }
    },

    /**
     * VALID TO
     */
    {
      title: t('info.evidence.validTo'),
      dataIndex: 'evd_valid_to',
      key: 'evd_valid_to',
      width: 150,
      render: (value: string, row: RowEvidences) => {
        const hasSolution = !!row.solution || row.isNew;

        if (!hasSolution) {
          return {
            children: null,
            props: { rowSpan: 1 }
          };
        }

        return {
          children: (
            <CustomDatePicker
              value={row.evd_valid_to ? dayjs(row.evd_valid_to) : null}
              onChange={(date: any) =>
                updateRow(
                  row.key,
                  'evd_valid_to',
                  date ? date.format('YYYY-MM-DD') : null
                )
              }
            />
          ),
          props: { rowSpan: 1 }
        };
      }
    }
  ];

  return (
    <ComplianceEstLayout>
      <Styled.ContentWrapper token={token}>
        <Table<RowEvidences>
          columns={columns}
          dataSource={pageData}
          bordered
          rowKey={(row) => row.key}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize: 10,
            total: dataWithSpan.length,
            showSizeChanger: false,
            position: ['bottomRight'],
            onChange: (p) => setPage(p)
          }}
        />
      </Styled.ContentWrapper>
    </ComplianceEstLayout>
  );
};

export default EvidenceDefinition;
